import { useCallback, useEffect, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import AgGrid from "../../../UI/AgGrid";
import {
  ColDef,
  GridReadyEvent,
  RowDoubleClickedEvent,
  IServerSideGetRowsParams,
} from "ag-grid-community";
import { useQuotationColumnDefs } from "./columnDefs";
import { useNavigate } from "react-router-dom";
import { GraphQLClient } from "graphql-request";
import { createGraphQLClientWithMiddleware } from "../../../../services/graphqlClient";
import { dateFilterModel } from "../../../../utils/Formatter/AgGridFilter";
import { QUOTATION_AGGRID } from "../../../../services/AgGrid/SalesAgGrid";
import { useStateContext } from "../../../../contexts/auth-context";

interface Props {
  isFilter?: string | null;
  referenceUniqueId?: string;
}

const QuotationTable = ({ isFilter, referenceUniqueId }: Props) => {
  const gridRef = useRef<AgGridReact<any>>(null);
  const navigate = useNavigate();

  const {
    state: { authUser, permissions },
  } = useStateContext();

  const branch_id = parseInt(sessionStorage.getItem("branch-id") || "");

  const columnDef: ColDef[] | undefined = useQuotationColumnDefs();

  const graphQLClientWithHeaderSales: GraphQLClient =
    createGraphQLClientWithMiddleware("sales");

  const datasource = {
    async getRows(params: IServerSideGetRowsParams) {
      const { request } = params;
      const { startRow, endRow, filterModel, sortModel } = request;
      const {
        created_date,
        issue_date,
        due_date,
        created_by_object,
        related_user_object_list,
        tag_list,
        ...otherFilterModel
      } = filterModel;

      const orConditions = [
        {
          created_by_unique_id: permissions?.quotation?.view_self
            ? {
                filterType: "text",
                type: "equals",
                filter: authUser?.unique_id,
              }
            : undefined,
        },
        permissions?.quotation?.view_self
          ? {
              related_user_unique_id_list: {
                filterType: "array",
                type: "hasSome",
                values: [authUser?.unique_id],
              },
            }
          : undefined,
      ].filter(
        (item) =>
          item !== undefined &&
          Object.keys(item).length > 0 &&
          !Object.values(item).every((value) => value === undefined)
      );

      const formatFilter = {
        ...otherFilterModel,
        issue_date: dateFilterModel(issue_date),
        due_date: dateFilterModel(due_date),
        created_by: created_by_object,
        related_user_list: related_user_object_list
          ? {
              filterType: "array",
              type: "hasSome",
              values: related_user_object_list.values,
            }
          : undefined,
        tag_list: tag_list
          ? {
              filterType: "array",
              type: "hasSome",
              values: tag_list.values,
            }
          : undefined,
        branch_id:
          branch_id !== -1
            ? {
                filterType: "number",
                type: "equals",
                filter: branch_id,
              }
            : undefined,
        _or:
          orConditions?.length > 0
            ? {
                _orArray: orConditions,
              }
            : undefined,
      };
      try {
        const { QuotationViewsAggrid } =
          await graphQLClientWithHeaderSales.request(QUOTATION_AGGRID, {
            aggridInput: {
              startRow,
              endRow,
              filterModel: formatFilter,
              sortModel,
            },
          });
        params.success({
          rowData: QuotationViewsAggrid.results as any[],
          rowCount: QuotationViewsAggrid.count as number,
        });
      } catch (err) {
        params.fail();
      }
    },
  };

  const onFilterChanged = useCallback(
    (params: any) => {
      const instance = params.api.getFilterInstance("status");
      switch (isFilter) {
        case "draft":
          instance?.setModel({ values: ["draft"] });
          break;
        case "wait_approve":
          instance?.setModel({ values: ["wait_approve"] });
          break;
        case "not_approved":
          instance?.setModel({ values: ["not_approved"] });
          break;
        case "wait_accept":
          instance?.setModel({ values: ["wait_accept"] });
          break;
        case "accepted":
          instance?.setModel({ values: ["accepted"] });
          break;
        case "finished":
          instance?.setModel({ values: ["finished"] });
          break;
        case "cancelled":
          instance?.setModel({ values: ["cancelled"] });
          break;
        default:
          instance?.setModel({});
          break;
      }
      params.api.onFilterChanged();
    },
    [isFilter]
  );

  const onGridReady = (params: GridReadyEvent) => {
    onFilterChanged(params);
    params.api.setServerSideDatasource(datasource);
  };

  useEffect(() => {
    if (gridRef.current && gridRef.current.api) {
      onFilterChanged(gridRef.current);
    }
  }, [gridRef, onFilterChanged]);

  const onRowDoubleClicked = (params: RowDoubleClickedEvent) => {
    navigate(`/sales/quotation/${encodeURIComponent(params.data.id)}`);
  };

  return (
    <AgGrid
      ref={gridRef}
      columnDefs={columnDef}
      height={665}
      onRowDoubleClicked={onRowDoubleClicked}
      onGridReady={onGridReady}
      path={"/sales/quotation"}
    />
  );
};

export default QuotationTable;
